/**
 * Parse working frame from coverage tool results (FE fallback when SSE missing).
 * Callers: InsightsChat, InsightsWorkingFramePanel tests.
 * Display strings are agent-owned — no FE rewriting of terms/period/criteria.
 */
import type {
  InsightsFrameSource,
  InsightsSelectionCriteria,
  InsightsWorkingFrame,
} from "../types";

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((v) => (typeof v === "string" ? v.trim() : "")).filter(Boolean);
}

function parseSelectionCriteria(raw: unknown): InsightsSelectionCriteria | undefined {
  const row = asRecord(raw);
  if (!row) return undefined;
  const population = asString(row.population);
  const period = asString(row.period);
  const systemsRaw = row.systems;
  const systems: NonNullable<InsightsSelectionCriteria["systems"]> = [];
  if (Array.isArray(systemsRaw)) {
    for (const item of systemsRaw) {
      const s = asRecord(item);
      if (!s) continue;
      const platform = asString(s.platform);
      const criteria = asString(s.criteria);
      if (!platform || !criteria) continue;
      const entity = asString(s.entity);
      systems.push({
        platform,
        ...(entity ? { entity } : {}),
        criteria,
      });
    }
  }
  if (!population && !period && !systems.length) return undefined;
  const filters = asStringArray(row.filters);
  return {
    population: population ?? "",
    period: period ?? "",
    ...(filters.length ? { filters } : {}),
    ...(systems.length ? { systems } : {}),
  };
}

function parseFrameSource(raw: unknown): InsightsFrameSource | undefined {
  if (raw === "contract" || raw === "provisional" || raw === "user") return raw;
  return undefined;
}

export function workingFrameFromCoveragePayload(
  payload: unknown,
  prior?: InsightsWorkingFrame | null,
  opts?: { updatedAt?: string },
): InsightsWorkingFrame | undefined {
  const row = asRecord(payload);
  if (!row) return prior ?? undefined;

  const definition = asString(row.definition) ?? prior?.definition ?? "";
  const readingsRaw = row.term_readings ?? row.termReadings;
  const readings: Array<{ term: string; meaning: string }> = [];
  if (Array.isArray(readingsRaw)) {
    for (const item of readingsRaw) {
      const r = asRecord(item);
      if (!r) continue;
      const term = asString(r.term);
      const meaning = asString(r.meaning);
      if (term && meaning) readings.push({ term, meaning });
    }
  }
  const assumptions = asStringArray(row.assumptions);
  const selectionCriteria =
    parseSelectionCriteria(row.selection_criteria ?? row.selectionCriteria) ??
    prior?.selectionCriteria;
  const frameSource =
    parseFrameSource(row.frame_source ?? row.frameSource) ?? prior?.frameSource ?? "provisional";
  const contractIds = asStringArray(row.contract_ids ?? row.contractIds);
  const nextContractIds = contractIds.length > 0 ? contractIds : prior?.contractIds;
  const updatedAt =
    asString(row.updated_at ?? row.updatedAt) ?? asString(opts?.updatedAt) ?? prior?.updatedAt;

  if (!definition && !readings.length && !assumptions.length && !selectionCriteria && !prior) {
    return undefined;
  }

  return {
    definition: definition || prior?.definition || "",
    termReadings: readings.length > 0 ? readings : (prior?.termReadings ?? []),
    assumptions: assumptions.length > 0 ? assumptions : (prior?.assumptions ?? []),
    ...(selectionCriteria ? { selectionCriteria } : {}),
    frameSource,
    ...(nextContractIds?.length ? { contractIds: nextContractIds } : {}),
    ...(updatedAt ? { updatedAt } : {}),
  };
}

/** Rebuild working frame from tool activity (last coverage write wins). */
export function workingFrameFromToolActivity(
  items: Array<{
    kind: string;
    activity?: {
      tool: string;
      phase: string;
      args?: unknown;
      result?: unknown;
      endedAt?: string;
      startedAt?: string;
      createdAt?: string;
    };
  }>,
): InsightsWorkingFrame | null {
  let frame: InsightsWorkingFrame | undefined;
  for (const item of items) {
    if (item.kind !== "tool" || !item.activity) continue;
    const { tool, phase, args, result, endedAt, startedAt, createdAt } = item.activity;
    if (tool !== "update_investigation_coverage") continue;
    const payload = phase === "result" ? result : args;
    const stamp = endedAt || startedAt || createdAt;
    const next = workingFrameFromCoveragePayload(payload, frame, {
      ...(stamp ? { updatedAt: stamp } : {}),
    });
    if (next) frame = next;
  }
  return frame ?? null;
}

/**
 * Prefer the newer of host SSE/HITL vs tool-activity rebuild so mid-run
 * definition updates land in the sidebar.
 */
export function preferWorkingFrame(
  host: InsightsWorkingFrame | null | undefined,
  parsed: InsightsWorkingFrame | null | undefined,
): InsightsWorkingFrame | null {
  const h = host?.definition?.trim() ? host : null;
  const p = parsed?.definition?.trim() ? parsed : null;
  if (!h) return p;
  if (!p) return h;
  const hostTs = Date.parse(h.updatedAt ?? "") || 0;
  const parsedTs = Date.parse(p.updatedAt ?? "") || 0;
  if (hostTs !== parsedTs) return hostTs > parsedTs ? h : p;
  // Same stamp or both missing — prefer parsed (last coverage write in transcript).
  return p;
}

export function frameSourceLabel(source: InsightsFrameSource | undefined): string {
  switch (source) {
    case "contract":
      return "Org metric";
    case "user":
      return "Confirmed";
    case "provisional":
    default:
      return "Provisional";
  }
}
